import { Icon } from '@eventbrite/eds-icon';
import { gettext } from '@eventbrite/i18n';
import SymbolTrendingUp from '@eventbrite/marmalade-icons/react/SymbolTrendingUp';
import ToggleHeartOn from '@eventbrite/marmalade-icons/react/ToggleHeartOn';
import React from 'react';
import { useCreatorQuality } from '../../hooks/useCreatorQualityQuery';
import { getFormattedNumber, getSignalValue } from '../../utils';
import { getTrustSignalsExperiment } from '../../utils/statsig';
import './OrganizerQualitySignals.scss';

interface OrganizerQualitySignalsProps {
    organizerId: string;
}

const SIGNALS_TO_SHOW = [
    {
        hypothesisName: 'attendeesHosted',
        id: 'attendees-hosted',
        icon: <SymbolTrendingUp />,
        getText: (count: number) =>
            `${getFormattedNumber(count)} ${gettext('attendees hosted')}`,
    },
    {
        hypothesisName: 'repeatAttendees',
        id: 'repeat-customers',
        icon: <ToggleHeartOn />,
        getText: () => gettext('Lots of repeat customers'),
    },
] as const;

export const OrganizerQualitySignals: React.FunctionComponent<
    OrganizerQualitySignalsProps
> = ({ organizerId }) => {
    const { qualitySignalData, isLoading, error } =
        useCreatorQuality(organizerId);

    if (isLoading || error) {
        return null;
    }

    const hypotheses = qualitySignalData?.hypothesis || [];

    const matchedSignalsOrNull = SIGNALS_TO_SHOW.map((signalConfig) => {
        const numericValue = getSignalValue(
            hypotheses,
            signalConfig.hypothesisName,
        );

        if (numericValue == null) {
            return null;
        }

        if (
            signalConfig.hypothesisName === 'attendeesHosted' &&
            numericValue <= 1000
        ) {
            return null;
        }

        if (
            signalConfig.hypothesisName === 'repeatAttendees' &&
            numericValue <= 20
        ) {
            return null;
        }

        const signalToDisplay = {
            id: signalConfig.id,
            icon: signalConfig.icon,
            text: signalConfig.getText(numericValue),
        };

        return signalToDisplay;
    });

    const signalsToShow = matchedSignalsOrNull.filter(
        (signal) => signal !== null,
    );

    // Only run the experiment if there are actually signals to show
    const shouldRunExperiment = signalsToShow.length > 0;
    const trustSignalsVariant = shouldRunExperiment
        ? getTrustSignalsExperiment()
        : 'control';
    const shouldShowSignals = trustSignalsVariant === 'test';

    if (!shouldShowSignals || signalsToShow.length === 0) {
        return null;
    }

    return (
        <div
            className="organizer-quality-signals"
            data-testid="organizer-quality-signals"
        >
            {signalsToShow.map((signal) => (
                <span
                    key={signal?.id}
                    className="organizer-quality-signals__pill"
                >
                    <Icon size="xsmall" type={signal?.icon} />
                    <span>{signal?.text}</span>
                </span>
            ))}
        </div>
    );
};

export default OrganizerQualitySignals;
